import { MetricsService, ServerSummary } from '@activia/cm-api';
import { Injectable } from '@angular/core';
import { TimerService } from '@amp/messenger';
import { Observable, Subject, Subscription } from 'rxjs';
import { ErrorHandlingService } from '@amp/error';
import { ITimeSeriesDataSerie } from '../models/time-series-data-serie.interface';

@Injectable()
export class ServerOverviewService {
  public static refreshDelay: number = 1000 * 10;
  public static dataSetLimit = 60;

  private subscriberCount = 0;

  private timerSubscription: Subscription;

  private _serverProcess = new Subject<any>();
  private _serverOverview = new Subject<ServerSummary>();

  private _latestNetworkReceiveData = new Subject<ITimeSeriesDataSerie>();
  private _latestNetworkSendData = new Subject<ITimeSeriesDataSerie>();

  private _latestCpuUptime = new Subject<any>();
  private _cpuCoreCount = new Subject<number>();
  private _latestCpuLoad = new Subject<ITimeSeriesDataSerie>();
  private _latestDiskUsage = new Subject<ITimeSeriesDataSerie>();
  private _latestMemoryUsage = new Subject<ITimeSeriesDataSerie>();

  private _cpuLoad = 0;
  private _memPercent = 0;
  private _diskPercent = 0;

  private _cpuLoadSub = new Subject<number>();
  private _memPercentSub = new Subject<number>();
  private _diskPercentSub = new Subject<number>();

  constructor(private statsService: MetricsService, private errorHandlingService: ErrorHandlingService, private timerService: TimerService) {}

  start() {
    this.checkTimer();
    this.subscriberCount++;
  }

  stop() {
    this.subscriberCount--;
    if (this.subscriberCount <= 0) {
      if (this.timerSubscription) {
        this.timerSubscription.unsubscribe();
        this.timerSubscription = null;
      }
    }
  }

  get latestNetworkReceive$(): Observable<ITimeSeriesDataSerie> {
    return this._latestNetworkReceiveData.asObservable();
  }

  get latestNetworkSend$(): Observable<ITimeSeriesDataSerie> {
    return this._latestNetworkSendData.asObservable();
  }

  get latestCpuUptime$(): Observable<number> {
    return this._latestCpuUptime.asObservable();
  }

  get latestCpuLoad$(): Observable<ITimeSeriesDataSerie> {
    return this._latestCpuLoad.asObservable();
  }

  get cpuCoreCount$(): Observable<number> {
    return this._cpuCoreCount.asObservable();
  }

  get latestDiskUsage$(): Observable<ITimeSeriesDataSerie> {
    return this._latestDiskUsage.asObservable();
  }

  get latestMemoryUsage$(): Observable<ITimeSeriesDataSerie> {
    return this._latestMemoryUsage.asObservable();
  }

  get serverOverview$(): Observable<ServerSummary> {
    return this._serverOverview.asObservable();
  }

  get serverProcess$(): Observable<any> {
    return this._serverProcess.asObservable();
  }

  get cpuLoad$(): Observable<number> {
    return this._cpuLoadSub.asObservable();
  }

  get memPercent$(): Observable<number> {
    return this._memPercentSub.asObservable();
  }

  get diskPercent$(): Observable<number> {
    return this._diskPercentSub.asObservable();
  }

  private checkTimer() {
    if (!this.timerSubscription) {
      const timer = this.timerService.timer$(ServerOverviewService.refreshDelay);
      this.timerSubscription = timer.subscribe((_) => this.refresh());
    }
  }

  private refresh() {
    this.statsService.getServerSummary().subscribe(
      (overview: ServerSummary) => {
        this._cpuLoad = overview.cpuLoad;
        this._memPercent = overview.memoryTotal > 0 ? ((overview.memoryTotal - overview.memoryAvailable) / overview.memoryTotal) * 100 : 0;
        this._diskPercent = overview.diskTotal > 0 ? ((overview.diskTotal - overview.diskAvailable) / overview.diskTotal) * 100 : 0;

        this._latestCpuUptime.next(overview.cpuUptime);
        this._cpuCoreCount.next(overview.cpuCoreCountLogical);
        this._cpuLoadSub.next(this._cpuLoad ? this._cpuLoad : 0);
        this._memPercentSub.next(this._memPercent ? this._memPercent : 0);
        this._diskPercentSub.next(this._diskPercent ? this._diskPercent : 0);

        // update time series
        this.updateTimeSeries(overview);
        this._serverOverview.next(overview);
      },
      (err) => {
        if (err.status !== 0) {
          this.errorHandlingService.catchError(err, undefined, 'global.error.fetch-server-summary-fail');
        }
        this.onError();
      }
    );

    this.statsService.getServerDetails().subscribe(
      (details) => {
        this._serverProcess.next(details.processes);
      },
      (err) => {
        if (err.status !== 0) {
          this.errorHandlingService.catchError(err, undefined, 'global.error.fetch-server-details-fail');
        }
        this.onError();
      }
    );
  }

  private updateTimeSeries(overview: ServerSummary) {
    const date = new Date(Date.now());

    this._latestNetworkReceiveData.next({ x: date.getTime(), y: Number(overview.networkReceive) });
    this._latestNetworkSendData.next({ x: date.getTime(), y: Number(overview.networkSend) });

    this._latestCpuLoad.next({ x: date.getTime(), y: this._cpuLoad });
    this._latestDiskUsage.next({ x: date.getTime(), y: this._diskPercent });
    this._latestMemoryUsage.next({ x: date.getTime(), y: this._memPercent });
  }

  private onError() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }
}
